import { useEffect, useState } from 'react';
import { Lightbulb, Loader2 } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import { dispositivosDe, olvidarDispositivos } from './dispositivosCache';

// «Iluminar ubicación»: enciende la charola exacta desde la ficha, desde
// la búsqueda y desde la respuesta del asistente.
//
// LA REGLA DE ESTE BOTÓN: si no hay una tira instalada que alcance esa
// ubicación Y esté respondiendo, EL BOTÓN NO APARECE. No aparece gris, no
// aparece con un aviso: no aparece. Un botón que se pulsa y no enciende
// nada se deja de usar a la tercera vez — y para entonces tampoco se usan
// los que sí funcionan.
//
// Y NO APARECER NO ROMPE NADA. La ficha, la búsqueda y el asistente
// funcionan exactamente igual sin luces; un almacén sin tiras se trabaja
// como ayer. Por eso el fallo de la consulta se traga en silencio: si no
// se pudo saber si hay aparato, se hace como si no lo hubiera.

export default function BotonIluminar({ ubicacion, compacto = false, color = 'verde' }) {
  const [tira, setTira] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [encendida, setEncendida] = useState(false);
  const { exito, error: avisoError } = useAvisos();

  useEffect(() => {
    if (!ubicacion) { setTira(null); return undefined; }
    let vivo = true;
    dispositivosDe(ubicacion).then((lista) => {
      if (!vivo) return;
      // La primera que además esté respondiendo. El backend las devuelve
      // de la más específica a la más general (la charola antes que el
      // pasillo), así que la primera viva es la que mejor alumbra.
      setTira((Array.isArray(lista) ? lista : []).find((d) => d.en_linea) || null);
    });
    return () => { vivo = false; };
  }, [ubicacion]);

  if (!tira) return null;

  async function encender() {
    setEnviando(true);
    try {
      const r = await api.post('/dispositivos/iluminar', { ubicacion, color, segundos: 20 });
      if (r.entregado) {
        setEncendida(true);
        // Los mismos 20 segundos que se le pidieron al aparato: cuando el
        // aviso se va, la luz ya se apagó sola.
        setTimeout(() => setEncendida(false), 20000);
        exito(`Se encendió la luz en ${ubicacion}. Se apaga sola en 20 segundos.`);
      } else {
        // «No entregado» no es un fallo del usuario, así que no se le
        // regaña: se le dice qué pasó y qué mirar.
        avisoError(r.detalle
          || 'No se pudo mandar la señal a la tira. Revisa que esté encendida.');
      }
    } catch (e) {
      avisoError(e instanceof ErrorApi ? e.message
        : 'No se pudo encender la luz. Vuelve a intentarlo.');
      // Si falló porque el aparato se cayó, la próxima vez que se pinte
      // el botón hay que volver a preguntar en vez de creerle a la copia.
      olvidarDispositivos(ubicacion);
    } finally {
      setEnviando(false);
    }
  }

  const Icono = enviando ? Loader2 : Lightbulb;
  return (
    <button
      type="button"
      onClick={encender}
      disabled={enviando}
      title={`Enciende la luz de ${ubicacion} en la tira «${tira.nombre}»`}
      className={`inline-flex items-center gap-1.5 rounded-lg font-semibold
                  border transition-colors disabled:opacity-60
                  ${compacto ? 'text-xs px-2 py-1' : 'text-sm px-3 py-2'}
                  ${encendida
                    ? 'bg-amber-100 border-amber-300 text-amber-900'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-amber-50 hover:border-amber-300'}`}
    >
      <Icono className={`h-4 w-4 shrink-0 ${enviando ? 'animate-spin' : ''}`} aria-hidden="true" />
      {/* El icono NUNCA va solo: una bombilla sin texto no dice si
          enciende una luz, si es una idea o si es la ayuda. */}
      <span>{encendida ? 'Encendida' : 'Iluminar ubicación'}</span>
    </button>
  );
}
